'use client'

import OfferLinkArrow from './offer-link-arrow'
import { SHOW_OFFER_RECOVERY } from '@/utils/offer-ui-flags'
import NatTermsSummary from './nat-terms-summary'
import OfferMessage from './offer-message'

import OfferUsd from './offer-usd'

import Link from 'next/link'
import { ArrowUpRight, Bitcoin, Check } from 'lucide-react'
import { EXPLORER_URL } from '@/utils/constants'
import type { Offer } from '@/utils/offers'
import styles from './offer-dialog.module.css'

/** Receipt values come from the activated offer, not the editable form. */
export default function OfferSuccessReceipt({
	offer,
	quantity,
	days,
	name,
	inscriptionId,
	collectionSlug,
	preview,
	onClose
}: {
	offer: Offer | null
	quantity: number
	days: number
	name: string
	inscriptionId?: string
	collectionSlug?: string
	preview?: React.ReactNode
	onClose: () => void
}) {
	if (!offer) return null
	const itemId = offer.inscription_id || inscriptionId
	const slug = offer.collection_slug || collectionSlug
	const href = itemId ? `/inscription/${itemId}#item-offers` : `/collection/${slug}`
	const expires = new Date(offer.expires_at)
	const openEnded = days >= 365
	const hasExpiry = !openEnded && Number.isFinite(expires.getTime())
	const btc = (offer.price_sats / 1e8).toLocaleString('en-US', { maximumFractionDigits: 8 })
	return (
		<section className={styles.receipt} aria-label="Offer receipt">
			<div className={styles.receiptHeading} role="status">
				<Check size={20} aria-hidden="true" />
				<h3>{quantity > 1 ? `${quantity} offers placed` : 'Offer placed'}</h3>
			</div>
			<Link className={styles.receiptCard} href={href} onClick={onClose}>
				{preview && (
					<div className={styles.receiptImage} aria-hidden="true">
						{preview}
					</div>
				)}
				<div className={styles.receiptTarget}>
					<strong>{name}</strong>
					<span>
						{offer.nat_terms
							? 'One exact-size NAT transfer lot'
							: offer.scope === 'trait'
							? `${offer.trait_type}: ${offer.trait_value}`
							: itemId
							? 'Item offer'
							: 'Any item in this collection'}
					</span>
					<span className={styles.receiptLink}>
						View {itemId ? 'item offers' : 'collection'}{' '}
						<ArrowUpRight size={14} aria-hidden="true" />
					</span>
				</div>
			</Link>
			<dl className={styles.receiptDetails}>
				<div>
					<dt>{quantity > 1 ? 'Offer per item' : 'Your offer'}</dt>
					<dd className={styles.receiptAmount}>
						<Bitcoin size={16} className={styles.bitcoin} aria-hidden="true" />
						{btc} BTC <OfferUsd sats={offer.price_sats} block />
						<NatTermsSummary offer={offer} />
					</dd>
				</div>
				{(offer.rebate_sats ?? 0) > 0 && (
					<div data-testid="receipt-rebate">
						<dt>Fee rebate, when filled</dt>
						<dd className={styles.rebate}>
							{((offer.rebate_sats ?? 0) / 1e8).toLocaleString('en-US', { maximumFractionDigits: 8 })} BTC{' '}
							<OfferUsd sats={offer.rebate_sats ?? 0} />
						</dd>
					</div>
				)}
				<div>
					<dt>Expires</dt>
					<dd>
						{Number.isFinite(expires.getTime())
							? expires.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
							: `${days} ${days === 1 ? 'day' : 'days'}`}
					</dd>
				</div>
				{quantity > 1 && (
					<div>
						<dt>Quantity</dt>
						<dd>{quantity} items</dd>
					</div>
				)}
				{hasExpiry && (
					<div className={styles.receiptExpiry}>
						<dt>Expires</dt>
						<dd>
							<time dateTime={expires.toISOString()}>
								{expires.toLocaleString(undefined, {
									year: 'numeric',
									month: 'short',
									day: 'numeric',
									hour: 'numeric',
									minute: '2-digit',
									timeZoneName: 'short'
								})}
							</time>
						</dd>
					</div>
				)}
			</dl>
			<OfferMessage offer={offer} message={offer.message} />
			{SHOW_OFFER_RECOVERY && (
				<p>
					<a href="/offers/recovery">
						Download your recovery backup <OfferLinkArrow />
					</a>
				</p>
			)}
			<p className={styles.hint}>Manage or cancel your offer from your wallet.</p>
			<div className={styles.receiptActions}>
				<Link href="/wallet#offers-sent" onClick={onClose}>
					Manage offer <ArrowUpRight size={14} aria-hidden="true" />
				</Link>
				{offer.funding_txid && (
					<a href={`${EXPLORER_URL}/tx/${offer.funding_txid}`} target="_blank" rel="noreferrer">
						View funding transaction <ArrowUpRight size={14} aria-hidden="true" />
					</a>
				)}
			</div>
		</section>
	)
}
